// Step 5: Review & Upload
import { useState, useEffect, useCallback, useMemo } from 'react';
import { CheckCircle, AlertCircle, Loader, RotateCcw } from 'lucide-react';
import { useUploadWizardContext } from '../context/UploadWizardContext';
import { formatBytes } from '@utils/format';
import { useFileUpload } from '@hooks/useFileUpload';
import { useFileStore } from '@stores/useFileStore';
import { useToast } from '@components/ui';
import { cn } from '@utils/classNames';
import { useAuthStore } from '@stores/authStore';
import { UploadGroupCard } from '../components/UploadGroupCard';
import { useUploadGroups } from '../hooks/useUploadGroups';
import { storageLimitService } from '@services/storage-limit.service';
import { UserPlan } from '@types';
import { getRoleForPlan } from '@config/subscription';
import type { UploadBatchItem } from '../types';

interface Step4ReviewProps {
  onUploadComplete?: () => void;
  onUploadHandlerSet?: (handler: () => void) => void;
}

function parseBpm(bpmStr: string): number | undefined {
  const bpm = parseInt(bpmStr, 10);
  if (isNaN(bpm) || bpm < 50 || bpm > 220) return undefined;
  return bpm;
}

export const Step4Review: React.FC<Step4ReviewProps> = ({
  onUploadComplete,
  onUploadHandlerSet,
}) => {
  const { data, reset } = useUploadWizardContext();
  const { uploadBatch } = useFileUpload();
  const { uploadProgress, files: storeFiles } = useFileStore();
  const { groups, getFileSize } = useUploadGroups();
  const { error: showError } = useToast();
  const [isUploadingState, setIsUploadingState] = useState(false);
  const [uploadStarted, setUploadStarted] = useState(false);
  const [failedFiles, setFailedFiles] = useState<Set<string>>(new Set());
  const { user } = useAuthStore();
  const userRole = useMemo(
    () => getRoleForPlan(user?.plan ?? UserPlan.FREE),
    [user?.plan]
  );

  const fileNames = useMemo(() => data.files.map((f) => f.name), [data.files]);

  const currentUploadProgress = useMemo(() => {
    const progressArray = Array.from(uploadProgress.values());
    return progressArray.filter((p) => fileNames.includes(p.fileName));
  }, [uploadProgress, fileNames]);

  const isUploadActive = useMemo(
    () =>
      currentUploadProgress.some(
        (p) => p.status === 'uploading' || p.status === 'pending' || p.status === 'retrying'
      ),
    [currentUploadProgress]
  );

  const overallProgress = useMemo(() => {
    if (currentUploadProgress.length === 0) return 0;
    const total = currentUploadProgress.reduce((sum, p) => sum + p.progress, 0);
    return Math.round(total / currentUploadProgress.length);
  }, [currentUploadProgress]);

  const buildBatchItems = useCallback((): UploadBatchItem[] => {
    const items: UploadBatchItem[] = [];
    const globalMeta = {
      beatstarsSellUrl: (data.beatstarsSellUrl || '').trim() || undefined,
      beatstarsPrice: (data.beatstarsPrice || '').trim() || undefined,
      collaborator: (data.collaborator || '').trim() || undefined,
    };

    for (const group of data.groups) {
      const bpm = parseBpm(group.bpm);
      const key = (group.musicalKey || '').trim() || undefined;

      for (const fileName of group.fileNames) {
        const file = data.files.find((f) => f.name === fileName);
        if (!file) continue;

        items.push({
          file,
          metadata: {
            groupId: group.id,
            isMainVersion: fileName === group.mainFileName,
            versionType: group.versionTypes[fileName] || undefined,
            bpm,
            key,
            tags: group.tags,
            status: data.fileStatus,
            ...globalMeta,
          },
        });
      }
    }
    return items;
  }, [data]);

  const runUpload = useCallback(async () => {
    if (isUploadingState) return;
    if (!user?.id) {
      showError('Login required', 'Please sign in again and try once more.');
      return;
    }
    if (data.files.length === 0) return;

    setIsUploadingState(true);
    setUploadStarted(true);
    setFailedFiles(new Set());

    try {
      const items = buildBatchItems();
      const validation = await storageLimitService.canUpload(
        user.id,
        userRole,
        storeFiles,
        items.map((i) => i.file)
      );

      if (!validation.canUpload) {
        showError('Upload blocked', validation.message || 'Storage limit reached');
        return;
      }

      const results = await uploadBatch(items, data.fileType);
      const failed = results.filter((r) => !r.success);
      const succeeded = results.filter((r) => r.success);

      if (failed.length > 0) {
        setFailedFiles(new Set(failed.map((f) => f.fileName)));
        if (succeeded.length === 0) {
          showError('Upload failed', failed[0]?.error || 'All uploads failed');
          return;
        }
        showError(
          'Partial upload',
          `${failed.length} of ${results.length} files failed. You can retry failed files.`
        );
        return;
      }

      if ((window as any).__uploadWizardMarkComplete) {
        (window as any).__uploadWizardMarkComplete();
      }

      reset();
      onUploadComplete?.();
    } catch (err) {
      showError('Upload failed', err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setIsUploadingState(false);
    }
  }, [
    isUploadingState,
    user?.id,
    userRole,
    data,
    buildBatchItems,
    uploadBatch,
    storeFiles,
    showError,
    reset,
    onUploadComplete,
  ]);

  const retryFailed = useCallback(async () => {
    if (!user?.id || failedFiles.size === 0) return;
    setIsUploadingState(true);

    try {
      const items = buildBatchItems().filter((i) => failedFiles.has(i.file.name));
      const results = await uploadBatch(items, data.fileType);
      const stillFailed = results.filter((r) => !r.success);

      if (stillFailed.length === 0) {
        setFailedFiles(new Set());
        if ((window as any).__uploadWizardMarkComplete) {
          (window as any).__uploadWizardMarkComplete();
        }
        reset();
        onUploadComplete?.();
      } else {
        setFailedFiles(new Set(stillFailed.map((f) => f.fileName)));
        showError('Retry failed', `${stillFailed.length} file(s) still failed`);
      }
    } finally {
      setIsUploadingState(false);
    }
  }, [user?.id, failedFiles, buildBatchItems, uploadBatch, data.fileType, showError, reset, onUploadComplete]);

  useEffect(() => {
    onUploadHandlerSet?.(runUpload);
  }, [onUploadHandlerSet, runUpload]);

  const totalSize = data.files.reduce((sum, file) => sum + file.size, 0);
  const showProgress = isUploadingState || isUploadActive || uploadStarted;

  return (
    <div className="space-y-3 sm:space-y-4 w-full">
      <div className="text-center mb-2 sm:mb-3">
        <h2 className="font-dela text-lg sm:text-xl text-white mb-1">Review & Upload</h2>
        <p className="text-white/60 font-inter text-sm sm:text-base">
          Review your groups and tap Confirm to upload
        </p>
      </div>

      {showProgress && (
        <div className="p-3 bg-white/5 border border-white/10 rounded-lg space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              {isUploadingState || isUploadActive ? (
                <Loader className="w-4 h-4 text-primary animate-spin" />
              ) : (
                <CheckCircle className="w-4 h-4 text-green-500" />
              )}
              <span className="font-inter font-semibold text-white text-sm">
                {isUploadingState || isUploadActive ? 'Uploading...' : 'Complete'}
              </span>
            </div>
            <span className="text-white/60 font-mono text-xs">{overallProgress}%</span>
          </div>
          <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden">
            <div
              className={cn(
                'h-full transition-all duration-300 rounded-full',
                isUploadingState || isUploadActive
                  ? 'bg-gradient-to-r from-primary to-[#00ff61]'
                  : 'bg-green-500'
              )}
              style={{ width: `${overallProgress}%` }}
            />
          </div>
          {currentUploadProgress.length > 0 && (
            <div className="space-y-1 max-h-32 overflow-y-auto">
              {data.files.map((file) => {
                const fp = currentUploadProgress.find((p) => p.fileName === file.name);
                const status = fp?.status ?? 'pending';
                const progress = fp?.progress ?? 0;
                return (
                  <div key={file.name} className="flex items-center justify-between text-xs">
                    <span className="text-white/80 truncate flex-1 mr-2">{file.name}</span>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {status === 'completed' && (
                        <CheckCircle className="w-3 h-3 text-green-500" />
                      )}
                      {status === 'error' && (
                        <AlertCircle className="w-3 h-3 text-red-500" />
                      )}
                      {status === 'uploading' && (
                        <Loader className="w-3 h-3 text-primary animate-spin" />
                      )}
                      <span className="text-white/50 font-mono w-8 text-right">{progress}%</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {failedFiles.size > 0 && (
        <button
          type="button"
          onClick={retryFailed}
          disabled={isUploadingState}
          className="w-full flex items-center justify-center gap-2 min-h-[44px] px-4 py-2 rounded-lg bg-red-500/20 text-red-400 border border-red-500/30 font-inter text-sm hover:bg-red-500/30 transition-all"
        >
          <RotateCcw className="w-4 h-4" />
          Retry {failedFiles.size} failed file{failedFiles.size !== 1 ? 's' : ''}
        </button>
      )}

      <div className="grid grid-cols-2 gap-2 p-3 bg-white/5 border border-white/10 rounded-lg text-xs sm:text-sm">
        <div>
          <span className="text-white/50">Files:</span>{' '}
          <span className="text-white font-semibold">{data.files.length}</span>
        </div>
        <div>
          <span className="text-white/50">Groups:</span>{' '}
          <span className="text-white font-semibold">{groups.length}</span>
        </div>
        <div>
          <span className="text-white/50">Type:</span>{' '}
          <span className="text-white font-semibold capitalize">{data.fileType}</span>
        </div>
        <div>
          <span className="text-white/50">Size:</span>{' '}
          <span className="text-white font-semibold">{formatBytes(totalSize)}</span>
        </div>
      </div>

      <div className="space-y-2">
        {groups.map((group) => (
          <UploadGroupCard
            key={group.id}
            group={group}
            mode="review"
            getFileSize={getFileSize}
          />
        ))}
      </div>
    </div>
  );
};
